import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import {
    Breadcrumb, Button, Form, Input, Space, Spin, message,
} from 'antd';
import {
    getDataCollection,
    updateDataCollection,
    getUsers,
} from '../services/api';
import { useUserSession } from '../contexts/UserSessionContext';
import CollectionOwnerSelect from '../components/collections/CollectionOwnerSelect';
import CollectionChatSearchSection from '../components/collections/CollectionChatSearchSection';
import SettingsField, { SettingsPageHead, SettingsPanel } from '../components/settings/SettingsField';
import './DataCollections.css';
import './AddAssistant.css';

const EditCollection = () => {
    const { collectionId } = useParams();
    const navigate = useNavigate();
    const { keycloak } = useKeycloak();
    const { isAdmin } = useUserSession();
    const chatRef = useRef(null);
    const [collection, setCollection] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [adminUsersMap, setAdminUsersMap] = useState({});
    const [adminUsersLoading, setAdminUsersLoading] = useState(false);
    const [settingsChapter, setSettingsChapter] = useState('general');

    const fetchCollection = useCallback(async () => {
        if (!keycloak?.authenticated || !collectionId) return;
        setLoading(true);
        try {
            const col = await getDataCollection(keycloak, collectionId);
            setCollection(col);
            setName(col?.name || '');
            setDescription(col?.description || '');
        } catch (err) {
            message.error(err.message || 'Failed to load collection');
            setCollection(null);
        } finally {
            setLoading(false);
        }
    }, [keycloak, collectionId]);

    useEffect(() => {
        fetchCollection();
    }, [fetchCollection]);

    useEffect(() => {
        if (!isAdmin || !collection || !keycloak?.authenticated) return;
        setAdminUsersLoading(true);
        getUsers(keycloak, null, 0, 2000)
            .then((list) => {
                const m = {};
                (list || []).forEach((u) => { m[u.id] = u.username || u.email || u.id; });
                setAdminUsersMap(m);
            })
            .catch(() => {})
            .finally(() => setAdminUsersLoading(false));
    }, [isAdmin, collection, keycloak]);

    const adminUserOptions = useMemo(
        () => Object.entries(adminUsersMap)
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        [adminUsersMap],
    );

    const showChatSection = collection && (collection.indexing_method === 'vector' || collection.indexing_method === 'hybrid');

    const handleSaveAll = async () => {
        if (!name.trim()) {
            setSettingsChapter('general');
            message.error('Name is required');
            return;
        }
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('name', name.trim());
            fd.append('description', description.trim());
            if (showChatSection && chatRef.current) {
                chatRef.current.appendRagToFormData(fd);
            }
            await updateDataCollection(keycloak, collectionId, fd);
            message.success('Collection updated');
            await fetchCollection();
        } catch (err) {
            message.error(err.message || 'Failed to update');
        } finally {
            setSaving(false);
        }
    };

    if (!collectionId) {
        return null;
    }

    if (!loading && !collection) {
        return (
            <div className="page-container add-assistant-page edit-collection-page">
                <div className="add-assistant-form-panel">
                    <Breadcrumb
                        className="add-assistant-breadcrumb"
                        items={[{ title: <Link to="/collections">Collections</Link> }, { title: 'Edit collection' }]}
                    />
                    <h1 className="add-assistant-page-title" style={{ marginTop: 12 }}>Collection not found</h1>
                    <Button type="primary" style={{ marginTop: 16 }} onClick={() => navigate('/collections')}>
                        Back to collections
                    </Button>
                </div>
            </div>
        );
    }

    const chapters = [
        { id: 'general', label: 'General' },
        ...(showChatSection ? [{ id: 'search', label: 'Search' }] : []),
    ];

    return (
        <div className="page-container add-assistant-page edit-collection-page">
            <div className="add-assistant-form-panel collections-table-wrapper edit-collection-panel">
                <Spin spinning={loading}>
                    {collection && (
                        <Form
                            layout="vertical"
                            className="add-assistant-form-inner"
                            onFinish={handleSaveAll}
                        >
                            <div className="settings-shell">
                                <nav className="settings-side" role="tablist" aria-label="Settings">
                                    {chapters.map((chapter) => (
                                        <button
                                            key={chapter.id}
                                            type="button"
                                            role="tab"
                                            aria-selected={settingsChapter === chapter.id}
                                            className={`settings-side-item${settingsChapter === chapter.id ? ' is-selected' : ''}`}
                                            onClick={() => {
                                                setSettingsChapter(chapter.id);
                                                window.scrollTo(0, 0);
                                            }}
                                        >
                                            {chapter.label}
                                        </button>
                                    ))}
                                </nav>
                                <div className="settings-main">
                                    <div className="add-assistant-page-intro">
                                        <Breadcrumb
                                            className="add-assistant-breadcrumb"
                                            items={[
                                                { title: <Link to="/collections">Collections</Link> },
                                                { title: <Link to={`/collections/${collectionId}`}>{collection.name}</Link> },
                                                { title: 'Edit' },
                                            ]}
                                        />
                                        <h1 className="add-assistant-page-title">Edit collection</h1>
                                        <p className="add-assistant-page-subtitle">
                                            Name, owner, and how chat search uses this collection.
                                        </p>
                                    </div>

                                    <div className={`settings-chapter${settingsChapter === 'general' ? ' is-active' : ''}`}>
                                        <SettingsPageHead
                                            title="General"
                                            description="Name, description, and who owns this collection."
                                        />
                                        <SettingsPanel>
                                            <SettingsField label="Name" help="Shown in the collection list and in chat.">
                                                <Input
                                                    value={name}
                                                    onChange={(e) => setName(e.target.value)}
                                                    placeholder="Product docs"
                                                    maxLength={255}
                                                />
                                            </SettingsField>
                                            <SettingsField label="Description" help="A short note on what this collection contains.">
                                                <Input.TextArea
                                                    value={description}
                                                    onChange={(e) => setDescription(e.target.value)}
                                                    placeholder="Release notes and support articles"
                                                    rows={3}
                                                />
                                            </SettingsField>
                                            {isAdmin && (
                                                <SettingsField label="Owner" help="The person who can change this collection.">
                                                    <CollectionOwnerSelect
                                                        ownerId={collection.owner_id}
                                                        options={adminUserOptions}
                                                        loading={adminUsersLoading}
                                                        collectionId={collection.id}
                                                        collectionName={collection.name}
                                                        keycloak={keycloak}
                                                        onUpdated={fetchCollection}
                                                        className="edit-collection-owner-select"
                                                        selectVariant="outlined"
                                                        getPopupContainer={() => document.body}
                                                    />
                                                </SettingsField>
                                            )}
                                        </SettingsPanel>
                                    </div>

                                    {showChatSection && (
                                        <div className={`settings-chapter${settingsChapter === 'search' ? ' is-active' : ''}`}>
                                            <SettingsPageHead
                                                title="Search"
                                                description="How chat looks up this collection before it answers."
                                            />
                                            <SettingsPanel padded>
                                                <CollectionChatSearchSection
                                                    ref={chatRef}
                                                    collection={collection}
                                                    keycloak={keycloak}
                                                    navigate={navigate}
                                                    disabled={saving}
                                                />
                                            </SettingsPanel>
                                        </div>
                                    )}

                                    <div className="add-assistant-form-footer">
                                        <Space wrap className="add-assistant-form-footer-actions">
                                            <Button onClick={() => navigate(`/collections/${collectionId}`)}>
                                                Cancel
                                            </Button>
                                            <Button type="primary" htmlType="submit" loading={saving}>
                                                Save changes
                                            </Button>
                                        </Space>
                                    </div>
                                </div>
                            </div>
                        </Form>
                    )}
                </Spin>
            </div>
        </div>
    );
};

export default EditCollection;
